import { EventTimeSpan } from './Event';
import { TaskStatusFilter } from './Task';

export type User = {
  uuid: string;
  email: string;
  organization_uuid: string;
};

export enum UserSettingsCollapseKeys {
  // accounts
  ACCOUNT_ITEM_INFORMATION = 'account_item_information',
  ACCOUNT_ITEM_ADDRESS_INFORMATION = 'account_item_address_information',
  ACCOUNT_ITEM_CONTACTS = 'account_item_contacts',
  ACCOUNT_ITEM_EVENTS_TIMELINE = 'account_item_events_timeline',
  ACCOUNT_ITEM_TASKS = 'account_item_tasks',
  ACCOUNT_ITEM_NOTES = 'account_item_notes',
  ACCOUNT_ITEM_ATTACHMENTS = 'account_item_attachments',
  ACCOUNT_ITEM_TAGS = 'account_item_tags',
  ACCOUNT_ITEM_ACTIVITY_LOG = 'account_item_activity_logs',

  // events
  EVENT_ITEM_INFORMATION = 'event_item_information',
  EVENT_ITEM_CUSTOM_FIELDS = 'event_item_custom_fields',
  EVENT_ITEM_TASKS = 'event_item_tasks',
  EVENT_ITEM_EMPLOYEES = 'event_item_employees',
  EVENT_ITEM_CONTACTS = 'event_item_contacts',
  EVENT_ITEM_NOTES = 'event_item_notes',
  EVENT_ITEM_ATTACHMENTS = 'event_item_attachments',
  EVENT_ITEM_EMAIL_TEMPLATES = 'event_item_email_templates',
  EVENT_ITEM_INTEGRATIONS = 'event_item_integrations',
  EVENT_ITEM_TAGS = 'event_item_tags',
  EVENT_ITEM_ACTIVITY_LOG = 'event_item_activity_logs',

  // contacts
  CONTACT_ITEM_INFORMATION = 'contact_item_information',
  CONTACT_ITEM_NOTES = 'contact_item_notes',
  CONTACT_ITEM_ATTACHMENTS = 'contact_item_attachments',
  CONTACT_ITEM_TAGS = 'contact_item_tags',
  CONTACT_ITEM_ACTIVITY_LOG = 'contact_item_activity_logs',

  // employees
  EMPLOYEE_ITEM_INFORMATION = 'employee_item_information',
  EMPLOYEE_ITEM_EVENTS_TIMELINE = 'employee_item_events_timeline',
  EMPLOYEE_ITEM_NOTES = 'employee_item_notes',
  EMPLOYEE_ITEM_ATTACHMENTS = 'employee_item_attachments',
  EMPLOYEE_ITEM_TAGS = 'employee_item_tags',
  EMPLOYEE_ITEM_ACTIVITY_LOG = 'employee_item_activity_logs',

  // tasks
  TASK_ITEM_INFORMATION = 'task_item_information',
  TASK_ITEM_CUSTOM_FIELDS = 'task_item_fields',
  TASK_ITEM_STEPS = 'task_item_steps',
  TASK_ITEM_NOTES = 'task_item_notes',
  TASK_ITEM_ATTACHMENTS = 'task_item_attachments',
  TASK_ITEM_TAGS = 'task_item_tags',
  TASK_ITEM_ACTIVITY_LOG = 'task_item_activity_logs',

  // preferences
  PREFERENCES_ACCOUNT_STATUS = 'preferences_account_status',
  PREFERENCES_ACCOUNT_SCHOOL_DISTRICT = 'preferences_account_school_district',
  PREFERENCES_ACCOUNT_TYPE = 'preferences_account_type',
  PREFERENCES_EVENT_TYPES = 'preferences_event_types',
  PREFERENCES_EVENT_LOCATIONS = 'preferences_event_locations',
  PREFERENCES_TASK_TYPE = 'preferences_task_type',
  PREFERENCES_CONTACT_ROLE = 'preferences_contact_role',
  PREFERENCES_CONTACT_TITLE = 'preferences_contact_title',
  PREFERENCES_EMPLOYEE_ROLE = 'preferences_employee_role',
  PREFERENCES_EMPLOYEE_TITLE = 'preferences_employee_title',
  PREFERENCES_TAG = 'preferences_tag',
  PREFERENCES_ADDRESS_TYPE = 'preferences_address_type',

  // task templates
  TASK_TEMPLATES_ITEM_INFORMATION = 'task_templates_item_information',
  TASK_TEMPLATES_ITEM_STEPS = 'task_templates_item_steps',
  TASK_TEMPLATES_ITEM_FIELDS = 'task_templates_item_fields',
  TASK_TEMPLATES_ITEM_TAGS = 'task_templates_item_tags',

  // event templates
  EVENT_TEMPLATE_ITEM_INFORMATION = 'event_template_item_information',
  EVENT_TEMPLATE_TASK_TEMPLATES = 'event_template_task_templates',
  EVENT_TEMPLATE_ITEM_FIELDS = 'event_template_item_fields',
  EVENT_TEMPLATE_EMAIL_TEMPLATES = 'event_template_email_templates',
  EVENT_TEMPLATE_INTEGRATIONS = 'event_template_integrations',
  EVENT_TEMPLATE_ITEM_TAGS = 'event_template_item_tags',

  // email templates
  EMAIL_TEMPLATE_ITEM_INFORMATION = 'email_template_item_information',
  EMAIL_TEMPLATE_ITEM_CONTENT = 'email_template_item_content',
  EMAIL_TEMPLATE_ITEM_TAGS = 'email_template_item_tags',

  // organizations
  ORGANIZATION_ITEM_INFORMATION = 'organization_item_information',
  ORGANIZATION_ITEM_SETTINGS = 'organization_item_settings',
  USER_ROLE_ITEM_INFORMATION = 'user_role_item_information',
}

export enum UserConfigurableModule {
  ACCOUNTS = 'accounts',
  EVENTS = 'events',
  EVENTS_CALENDAR = 'events_calendar',
  TASKS = 'tasks',
  CONTACTS = 'contacts',
  EMPLOYEES = 'employees',
  TASK_TEMPLATES = 'task_templates',
  EVENT_TEMPLATES = 'event_templates',
  EMAIL_TEMPLATES = 'email_templates',
}

export type CollapseSettings = Record<string, boolean>;
export type SortSettings = Partial<Record<UserConfigurableModule, Record<string, 1 | -1>>>;
export type IntegrationsSettings = Record<string, Record<string, any>>;

export type OwnershipSetting = 'my' | 'all';
export interface DashboardModuleFilters {
  span?: EventTimeSpan;
  status?: TaskStatusFilter;
}
export interface DashboardModuleSettings {
  ownership: OwnershipSetting;
  filters: DashboardModuleFilters;
}
export type DashboardSettings = Partial<Record<UserConfigurableModule, DashboardModuleSettings>>;

export interface ColumnSetting {
  key: string;
}
export type ListColumnSettings = Partial<Record<UserConfigurableModule, ColumnSetting[]>>;

export type FilterSelectionSettings = Partial<Record<UserConfigurableModule, string>>;

export type UserSettings = {
  collapse: CollapseSettings;
  sorts: SortSettings;
  integrations: IntegrationsSettings;
  dashboard: DashboardSettings;
  list_columns: ListColumnSettings;
  filter_selection: FilterSelectionSettings;
  isAdmin?: boolean;
};

export type CollapsePayload = Pick<UserSettings, 'collapse'>;
export type SortPayload = Pick<UserSettings, 'sorts'>;
export type DashboardSettingPayload = Pick<UserSettings, 'dashboard'>;
export type ColumnPayload = Pick<UserSettings, 'list_columns'>;

export interface UserSettingsResponse {
  settings: UserSettings;
}
